import Fuse from 'fuse.js';
import { trainingData } from './trainingData';

class ChatEngine {
  constructor() {
    this.language = null; // null = not selected yet
    this.selectedPackage = null;
    this.questionCount = {};
    this.lastActivity = Date.now();
    this.fuseInstances = {};
    this.init();
  }

  init() {
    // Build Fuse.js search index for each language
    ['en', 'fr'].forEach((lang) => {
      const items = [];
      trainingData[lang].intents.forEach((intent) => {
        intent.patterns.forEach((pattern) => {
          items.push({ pattern: pattern.toLowerCase(), tag: intent.tag });
        });
      });

      this.fuseInstances[lang] = new Fuse(items, {
        keys: ['pattern'],
        threshold: 0.4, // fuzzy tolerance
        includeScore: true,
        minMatchCharLength: 2
      });
    });
  }

  setLanguage(lang) {
    this.language = lang;
  }

  getLanguage() {
    return this.language;
  }

  isWinterSeason() {
    const month = new Date().getMonth(); // 0-indexed
    // November (10) through April (3)
    return month >= 10 || month <= 3;
  }

  classifyIntent(message) {
    const lang = this.language || 'en';
    const msg = message.toLowerCase().trim();
    const msgWords = msg.split(/\s+/);
    const data = trainingData[lang];

    // 1. Try exact multi-word pattern substring match (longest wins)
    let bestMatch = null;
    let bestScore = 0;

    for (const intent of data.intents) {
      for (const pattern of intent.patterns) {
        const p = pattern.toLowerCase();
        // Full pattern found inside message
        if (msg.includes(p)) {
          // Multi-word patterns get extra bonus for specificity
          const wordCount = p.split(/\s+/).length;
          const score = p.length * 2 + (wordCount > 1 ? wordCount * 3 : 0);
          if (score > bestScore) {
            bestMatch = intent;
            bestScore = score;
          }
        }
      }
    }

    if (bestMatch && bestScore >= 4) {
      return bestMatch;
    }

    // 2. Word-level matching: check how many words from user message
    //    match words in each intent's patterns
    // First, build a word frequency map to know which words are common across intents
    const stopWords = new Set(['is', 'it', 'do', 'to', 'in', 'on', 'at', 'of', 'or', 'an', 'am', 'be', 'me', 'my', 'we', 'us', 'so', 'no', 'up', 'if', 'go', 'by', 'as', 'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out']);
    const wordIntentCount = {}; // how many intents each word appears in
    const intentWordSets = {};

    for (const intent of data.intents) {
      const allPatternWords = new Set();
      intent.patterns.forEach((p) => {
        p.toLowerCase().split(/\s+/).forEach((w) => {
          if (w.length >= 3 || (w.length >= 2 && !stopWords.has(w))) allPatternWords.add(w);
        });
      });
      intentWordSets[intent.tag] = allPatternWords;
      for (const w of allPatternWords) {
        wordIntentCount[w] = (wordIntentCount[w] || 0) + 1;
      }
    }

    const totalIntents = data.intents.length;
    const intentScores = {};
    for (const intent of data.intents) {
      let score = 0;
      const allPatternWords = intentWordSets[intent.tag];

      for (const word of msgWords) {
        if (word.length < 2) continue;
        if (word.length < 3 && stopWords.has(word)) continue;
        for (const pw of allPatternWords) {
          let baseScore = 0;
          // Exact word match
          if (word === pw) {
            baseScore = 3;
          }
          // Word starts with pattern word or vice versa (e.g. "packages" matches "package")
          else if (word.startsWith(pw) || pw.startsWith(word)) {
            baseScore = 2;
          }

          if (baseScore > 0) {
            // Weight by how unique this word is across intents
            // Words appearing in many intents get reduced score
            const intentFreq = wordIntentCount[pw] || 1;
            const uniqueness = Math.max(0.2, 1 - (intentFreq - 1) / totalIntents);
            score += baseScore * uniqueness;
          }
        }
      }

      if (score > 0) {
        intentScores[intent.tag] = score;
      }
    }

    // Find highest scoring intent from word matching
    let topTag = null;
    let topScore = 0;
    for (const [tag, score] of Object.entries(intentScores)) {
      if (score > topScore) {
        topScore = score;
        topTag = tag;
      }
    }

    if (topTag && topScore >= 2) {
      return data.intents.find((i) => i.tag === topTag);
    }

    // 3. Fuzzy search fallback using Fuse.js on individual words
    for (const word of msgWords) {
      if (word.length < 3) continue;
      const results = this.fuseInstances[lang].search(word);
      if (results.length > 0 && results[0].score < 0.35) {
        const matchedTag = results[0].item.tag;
        return data.intents.find((i) => i.tag === matchedTag);
      }
    }

    // 4. Fuzzy search on full message
    const results = this.fuseInstances[lang].search(msg);
    if (results.length > 0 && results[0].score < 0.45) {
      const matchedTag = results[0].item.tag;
      return data.intents.find((i) => i.tag === matchedTag);
    }

    return null;
  }

  trackQuestion(tag) {
    this.questionCount[tag] = (this.questionCount[tag] || 0) + 1;
  }

  shouldEscalate(tag) {
    // Escalate if user repeats same question 2+ times
    return (this.questionCount[tag] || 0) >= 2;
  }

  getResponse(userMessage) {
    this.lastActivity = Date.now();
    const lang = this.language || 'en';
    const data = trainingData[lang];

    const intent = this.classifyIntent(userMessage);

    if (!intent) {
      return {
        text: data.fallback,
        showBooking: data.fallbackShowBooking,
        isEscalation: false
      };
    }

    this.trackQuestion(intent.tag);

    // Track selected package
    if (['basic_package', 'super_package', 'deluxe_package'].includes(intent.tag)) {
      this.selectedPackage = intent.tag;
    }

    // Check if we should escalate (repeated questions)
    if (this.shouldEscalate(intent.tag) && !intent.escalation) {
      const escalationIntent = data.intents.find((i) => i.tag === 'special');
      if (escalationIntent) {
        const prefix = lang === 'en'
          ? 'It seems like you need more specific help. Let me connect you with our team.\n\n'
          : 'Il semble que vous ayez besoin d\'une aide plus spécifique. Laissez-moi vous mettre en contact avec notre équipe.\n\n';
        return {
          text: prefix + escalationIntent.responses[0],
          showBooking: false,
          isEscalation: true
        };
      }
    }

    // Build response
    let responseText = intent.responses[Math.floor(Math.random() * intent.responses.length)];

    // Add winter upsell if seasonal and it's winter
    if (this.isWinterSeason() && !intent.seasonal) {
      const winterIntent = data.intents.find((i) => i.tag === 'winter');
      if (
        winterIntent &&
        ['basic_package', 'super_package', 'services'].includes(intent.tag)
      ) {
        const winterTip = lang === 'en'
          ? '\n\n❄️ Winter tip: Salt buildup can damage your interior. Consider adding salt removal treatment!'
          : '\n\n❄️ Conseil hiver : L\'accumulation de sel peut endommager votre intérieur. Pensez à ajouter un traitement anti-sel!';
        responseText += winterTip;
      }
    }

    return {
      text: responseText,
      showBooking: intent.showBooking,
      isEscalation: intent.escalation || false,
      upsell: intent.upsell || null,
      tag: intent.tag
    };
  }

  getAbandonmentMessage() {
    const lang = this.language || 'en';
    return trainingData[lang].abandonment;
  }

  getBookingInfo() {
    const lang = this.language || 'en';
    return {
      text: trainingData[lang].bookNowText,
      link: trainingData[lang].bookingLink
    };
  }

  reset() {
    this.selectedPackage = null;
    this.questionCount = {};
    this.lastActivity = Date.now();
    // Don't reset language
  }
}

export default ChatEngine;
